// services/components/strategies/search-bar/SearchBar.state.tsx
'use client'

import React, { useEffect, useRef, useState } from 'react'
import SearchBar, { PlaceSuggestion } from './SearchBar'
import { getLocationManager } from '@/services/location/LocationManagerInstance'
import { getTrackingService } from '@/services/tracking/TrackingInstance'
import { calculateGeohash } from '@/utils/calculateGeohash'
import {
  fetchPlace,
  searchPlace,
} from '@/services/api/bff/places/Places.service'
import { UUID } from '@/utils/idGenerator'
import { useRouter } from 'next/navigation'

export type SearchBarStateProps = {
  id?: string
  placeholder: string
  autoFocus?: boolean
}

const QUERY_MIN_LENGTH = 3
const DEBOUNCE_MS = 200

export default function SearchBarState({
  id = UUID(),
  placeholder,
  autoFocus = false,
}: SearchBarStateProps) {
  const router = useRouter()
  const locationManager = getLocationManager()
  const tracking = getTrackingService()

  const [selected, setSelected] = useState<PlaceSuggestion | null>(null)
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([])
  const [defaults, setDefaults] = useState<PlaceSuggestion[]>([]) // ← keep defaults here
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [noResults, setNoResults] = useState(false)

  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)

  // Build defaults now and whenever LocationManager updates
  useEffect(() => {
    const update = () => {
      const top = locationManager.getTopFiveLocations()
      const next = top.map((loc) => ({
        id: loc.geohash,
        title: loc.name,
        url: loc.url,
      }))
      setDefaults(next)
    }
    update()
    const unsub = locationManager.subscribe?.(update)
    return () => {
      unsub?.()
    }
  }, [locationManager])

  // Show defaults initially (and whenever they change)
  useEffect(() => {
    if (query.trim().length < QUERY_MIN_LENGTH) {
      setSuggestions(defaults)
    }
  }, [defaults]) // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced remote search with cancellation
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (query.trim().length < QUERY_MIN_LENGTH) {
      if (abortRef.current) abortRef.current.abort()
      setLoading(false)
      setNoResults(false)
      setSuggestions(defaults) // ← use current defaults
      return
    }

    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort()
        const ac = new AbortController()
        abortRef.current = ac
        setLoading(true)
        setNoResults(false)

        const result = await searchPlace(query, { signal: ac.signal as any })
        const items = result?.data ?? []
        if (!items.length) {
          setSuggestions([])
          setNoResults(true)
        } else {
          setSuggestions(
            items.map((l: any) => ({
              id: l.attributes.location.geoHash.slice(0, 5),
              title: l.attributes.title,
              url: l.attributes.url,
            })),
          )
          setNoResults(false)
        }
      } catch (err: any) {
        if (err?.name !== 'AbortError') {
          console.error('Search failed', err)
          setNoResults(true)
        }
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, defaults])

  const select = (place: PlaceSuggestion | null) => {
    if (!place) return
    setSelected(place)
    tracking.addAndExecuteCommand(
      'location-search-submitted-suggestion',
      place.title,
    )
    router.push(place.url ?? '/')
  }

  const switchToFirstSuggestion = () => {
    if (suggestions.length > 0 && query.length >= QUERY_MIN_LENGTH) {
      router.push(suggestions[0].url ?? '/')
    }
  }

  const searchViaGPS = () => {
    if (!('geolocation' in navigator)) return
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const geohash = calculateGeohash(
            pos.coords.latitude,
            pos.coords.longitude,
          )
          const matchedLocation = await fetchPlace(
            undefined,
            undefined,
            geohash,
            'city',
          )
          const url = matchedLocation?.data?.attributes?.url
          if (url) router.push(url)
        } catch (err) {
          console.error('Failed to search via GPS', err)
        }
      },
      (error) => console.error('Geolocation error:', error),
      { enableHighAccuracy: true },
    )
  }

  return (
    <SearchBar
      id={id}
      selected={selected}
      suggestions={suggestions}
      placeholder={placeholder}
      autoFocus={autoFocus ?? false}
      loading={loading}
      noResults={noResults}
      setQuery={setQuery}
      setSelected={select}
      onClose={() => setQuery('')}
      onOpen={() => {
        tracking.addAndExecuteCommand('location-search-opened')
        if (query.trim().length < QUERY_MIN_LENGTH) setSuggestions(defaults)
      }}
      onSearchGPS={searchViaGPS}
      onEnter={switchToFirstSuggestion}
    />
  )
}
